import $ from "jquery";
import Util from "./../util";
import Tab from "./../tab";
import Variables from "./../variables";
import GoogleMaps from "./../googlemaps";
import formatNumber from "format-number";
import liquidjs from "./../liquidjs";
import infoWindowTemplate from "./templates/list-info-window.html";

const PropertyListView = (($) => {
  const Liquid = new liquidjs.Liquid();

  /** Formatters */
  const formatCurrency = formatNumber({ prefix: "$", round: 2 });
  const formatArea = formatNumber({ suffix: " sqft", round: 0 });

  /**
   * ------------------------------------------------------------------------
   * Constants
   * ------------------------------------------------------------------------
   */

  const NAME = "PropertyListView";
  const VERSION = "4.1.1";
  const DATA_KEY = "nh.propertyListView";
  const EVENT_KEY = `.${DATA_KEY}`;
  const DATA_API_KEY = ".data-api";
  const JQUERY_NO_CONFLICT = $.fn[NAME];

  const TemplateTypes = {
    infoWindow: {
      name: "infoWindow",
      default: infoWindowTemplate,
      dataTag: "listingMapInfoWindow",
    },
  };

  const NO_IMAGE_URL = "/_system/images/noimage.jpg";

  const Selector = {
    ROOT: "#nesthub-property-list-view",
    MODULE_INFO: "#nesthub-listing-page",
    SEARCH_FORM: "#nesthub-search-form",
    NAVIGATION: "#nesthub-property-list-tab-list",
    NAVIGATION_TABS: "li > ." + Variables.prefix + "-nav-link",
    MAP: "#nesthub-property-map-panel",
    PLUGIN_INFO: "#nesthub-plugin",
    FORM_KEYWORD: "#nesthub-listing-prop-search-keyword",
    FORM_MIN_RENT: "#nesthub-listing-prop-search-min-rent",
    FORM_MAX_RENT: "#nesthub-listing-prop-search-max-rent",
    FORM_BEDS: "#nesthub-listing-prop-search-beds",
    FORM_BATHS: "#nesthub-listing-prop-search-baths",
    FORM_PROPERTY_TYPE: "#nesthub-listing-prop-search-property-type",
    FORM_PETS: "#nesthub-listing-prop-search-pets",
    FORM_SORT: "#nesthub-listing-prop-search-sort",
    INFO_WINDOW_LINK: ".nhw-map-property-info-window",
  };

  const Api = {
    ENDPOINT: "/_system/api/listings",
  };

  const Modes = {
    LIST: "list",
    MAP: "map",
  };

  const DEFAULT_SOURCE_DATA = {
    type: "geojson",
    data: {
      type: "FeatureCollection",
      features: [],
    },
  };

  /**
   * ------------------------------------------------------------------------
   * Class Definition
   * ------------------------------------------------------------------------
   */

  class PropertyListView {
    constructor(element) {
      this._element = element;
      this._moduleInfo = null;

      this._cache = {
        templates: {},
      };

      this._searchForm = $(Selector.SEARCH_FORM, this._element)[0];
      this._navigation = $(Selector.NAVIGATION, this._element)[0];

      this._mode = Modes.LIST;

      this._map = null;
      this._mapInfoWindow = null;

      let moduleInfo = $(Selector.MODULE_INFO, this._element);

      if (moduleInfo.length > 0) {
        try {
          this._moduleInfo = JSON.parse(moduleInfo[0].innerHTML.trim());
        } catch (e) {
          this._moduleInfo = {};
        }
      }

      this._init();
    }

    // Getters

    static get VERSION() {
      return VERSION;
    }

    _getTemplate(type) {
      if (!this._cache.templates[type.name]) {
        this._cache.templates[type.name] = Liquid.parse(type.default);
      }
      return this._cache.templates[type.name];
    }

    // Public

    show() {}

    // Private

    _init() {
      const self = this;

      $(Selector.NAVIGATION_TABS, this._navigation).on(
        "click",
        function (event) {
          event.preventDefault();

          const $this = $(this);
          let data = $this.data(DATA_KEY);

          if (!data) {
            data = new Tab(this);
            $this.data(DATA_KEY, data);
          }

          if (this.id == "nesthub-property-map-tab") {
            self._showMap();
            self._mode = Modes.MAP;
          } else {
            self._mode = Modes.LIST;
          }

          data.show();
        }
      );

      $(this._element).on("click", Selector.INFO_WINDOW_LINK, function (event) {
        const url = $(this).data("url");
        if (url) {
          window.location.href = url;
        }
      });
    }

    _activate() {}

    _showMap() {
      const self = this;

      if (!this._map) {
        this._map = new GoogleMaps($(Selector.MAP, this._element)[0]);
        this._map.show(function (map) {
          self._map.onIdle((event) => {
            self._handleMapBoundsChange(event);
          });
        });
      }
    }

    _handleMapBoundsChange() {
      const self = this;

      if (!this._map) {
        return;
      }

      let mapInfo = this._map.getMapInfo();
      let formData = this._getFormData();

      let data = $.extend(
        {
          listingPageID: this._moduleInfo.pageID,
        },
        {
          latitudeMin: mapInfo.minLat,
          latitudeMax: mapInfo.maxLat,
          longitudeMin: mapInfo.minLon,
          longitudeMax: mapInfo.maxLon,
        },
        formData
      );

      let endpoint = Api.ENDPOINT;

      $.ajax(endpoint, {
        method: "GET",
        data: data,
      })
        .done(function (data, textStatus, jqXHR) {
          self._map.clearMarkers();

          data.listings.forEach(function (listing) {
            let marker = self._map.addMarker(
              parseFloat(listing.latitude),
              parseFloat(listing.longitude)
            );
            google.maps.event.addListener(marker, "click", () => {
              self._handleMarkerClick(marker, listing);
            });
          });
        })
        .fail(function (jqXHR, textStatus, errorThrown) {});
    }

    _handleMarkerClick(marker, listing) {
      if (!this._map) {
        return;
      }

      const data = {};

      const container = document.createElement("div");

      data.beds =
        listing.beds > 0
          ? listing.beds > 1
            ? listing.beds + " beds"
            : "1 bed"
          : "Studio";
      data.baths = listing.baths != 1 ? listing.baths + " baths" : "1 bath";
      data.area = listing.area > 0 ? formatArea(listing.area) : "";
      data.url = listing.systemUrl;
      data.imgUrl = `${listing.imageUrl}?size=small`;
      data.imgAlt = listing.url;
      data.amount = formatCurrency(listing.rent);

      const template = this._getTemplate(TemplateTypes.infoWindow);
      if (template) {
        Liquid.render(template, data).then((html) => {
          container.innerHTML = html;

          // InfoWindow renders outside this._element so event delegation won't
          // reach it — attach the navigation handler directly to the element.
          const link = container.querySelector(Selector.INFO_WINDOW_LINK);
          if (link) {
            link.style.cursor = "pointer";
            link.addEventListener("click", function () {
              const url = this.dataset.url;
              if (url) {
                window.location.href = url;
              }
            });
          }

          this._map.openInfoWindowOnMarker(container, marker);
        });
      }
    }

    _getFormData() {
      const self = this;
      let fields = [
        $(Selector.FORM_KEYWORD, this._element),
        $(Selector.FORM_MIN_RENT, this._element),
        $(Selector.FORM_MAX_RENT, this._element),
        $(Selector.FORM_BEDS, this._element),
        $(Selector.FORM_BATHS, this._element),
        $(Selector.FORM_PROPERTY_TYPE, this._element),
        $(Selector.FORM_PETS, this._element),
        $(Selector.FORM_SORT, this._element),
      ];
      let data = {};

      fields.forEach(function ($field) {
        if ($field.length == 0) {
          return;
        }

        let val = $field.val();
        if (val && val.trim() != "") {
          data[$field.attr("name")] = val;
        }
      });

      return data;
    }

    dispose() {
      $.removeData(this._element, DATA_KEY);
      this._element = null;
    }
  }

  $(Selector.ROOT).each(function () {
    const $this = $(this);
    let data = $this.data(DATA_KEY);

    if (!data) {
      data = new PropertyListView(this);
      $this.data(DATA_KEY, data);
    }
  });

  return PropertyListView;
})($);

export default PropertyListView;
